import { List }                         from 'immutable';
import PropTypes                        from 'prop-types';
import { connect }                      from 'react-redux';
import {
  branch,
  compose,
  setDisplayName,
  withProps,
  wrapDisplayName,
}                                       from 'recompose';
import mapResourceItem, { FETCH_BY_ID } from '../resources/mapResourceItem';
import { resourceNames }                from '../../config/resources.config';
import { userPropType }                 from '../../domain/user';
import {
  selectIsLoggedIn,
  selectJwtUserIdIfNotExpired
}                                       from '../../selectors/auth';
import {
  selectCurrentUser,
  selectCurrentUserCapabilities,
  selectCurrentUserLoadError,
  selectCurrentUserLoadInProgress
}                                       from '../../selectors/users';
import {
  MAP_RESOURCE_PROP_API_FETCH_ERROR,
  MAP_RESOURCE_PROP_API_IS_FETCHING,
  MAP_RESOURCE_PROP_RESOURCE_ID
}                                       from '../resources/mapResourceProps.const';
import UserFriendlyError                from '../../errors/UserFriendlyError';


const propPrefix                                           = 'currentUser';
export const MAP_CURRENT_USER_PROP_IS_LOGGED_IN            = `${propPrefix}IsLoggedIn`;
export const MAP_CURRENT_USER_PROP_CURRENT_USER_ID         = `${propPrefix}UserId`;
export const MAP_CURRENT_USER_PROP_IS_LOADING_USER         = `${propPrefix}IsLoadingUser`;
export const MAP_CURRENT_USER_PROP_LOAD_USER_ERROR         = `${propPrefix}LoadUserError`;
export const MAP_CURRENT_USER_PROP_CURRENT_USER            = `${propPrefix}`;
export const MAP_CURRENT_USER_PROP_IS_LOADING_CAPABILITIES = `${propPrefix}IsLoadingCapabilities`;
export const MAP_CURRENT_USER_PROP_LOAD_CAPABILITIES_ERROR = `${propPrefix}LoadCapabilitiesError`;
export const MAP_CURRENT_USER_PROP_USER_CAPABILITIES       = `${propPrefix}Capabilities`;
export const MAP_CURRENT_USER_PROP_FINISHED_LOADING        = `${propPrefix}FinishedLoading`;


export const MapCurrentUserPropTypes = {
  [MAP_CURRENT_USER_PROP_IS_LOGGED_IN]:      PropTypes.bool,
  [MAP_CURRENT_USER_PROP_CURRENT_USER_ID]:   PropTypes.string,
  [MAP_CURRENT_USER_PROP_IS_LOADING_USER]:   PropTypes.bool,
  [MAP_CURRENT_USER_PROP_LOAD_USER_ERROR]:   PropTypes.instanceOf(UserFriendlyError),
  [MAP_CURRENT_USER_PROP_CURRENT_USER]:      userPropType,
  [MAP_CURRENT_USER_PROP_USER_CAPABILITIES]: PropTypes.instanceOf(List),
  [MAP_CURRENT_USER_PROP_FINISHED_LOADING]:  PropTypes.bool,
};

export const mapCurrentUser = (/* Reserved for future config */) => (Component) => compose(
  setDisplayName(wrapDisplayName(Component, 'MapCurrentUser')),
  connect(mapCurrentUserState, {}),

  // Is the user is logged in, then we might need to load the user & capabilities.
  setDisplayName(wrapDisplayName(Component, 'LoadUser')),
  branch(
    ({[MAP_CURRENT_USER_PROP_IS_LOGGED_IN]: isLoggedIn}) => isLoggedIn,
    compose(
      // Load the user details
      setDisplayName(wrapDisplayName(Component, 'MapUserIdToResourceId')),
      withProps(props => ({[MAP_RESOURCE_PROP_RESOURCE_ID]: props[MAP_CURRENT_USER_PROP_CURRENT_USER_ID]})),
      mapResourceItem({resourceName: resourceNames.USERS, fetchBy: FETCH_BY_ID, propsWhitelist: []}),
      withProps(
        ({
           [MAP_RESOURCE_PROP_API_IS_FETCHING]: isLoading,
           [MAP_RESOURCE_PROP_API_FETCH_ERROR]: error,
         }) => ({
          [MAP_CURRENT_USER_PROP_IS_LOADING_USER]: isLoading,
          [MAP_CURRENT_USER_PROP_LOAD_USER_ERROR]: error,
        })
      ),

      // Load the user details
      setDisplayName(wrapDisplayName(Component, 'MapUserCapabilitiesIdToResourceId')),
      withProps(
        props => ({[MAP_RESOURCE_PROP_RESOURCE_ID]: `${props[MAP_CURRENT_USER_PROP_CURRENT_USER_ID]}/capabilities`})),
      mapResourceItem({resourceName: resourceNames.USERS, fetchBy: FETCH_BY_ID, propsWhitelist: []}),
      withProps(
        ({
           [MAP_RESOURCE_PROP_API_IS_FETCHING]: isLoading,
           [MAP_RESOURCE_PROP_API_FETCH_ERROR]: error,
         }) => ({
          [MAP_CURRENT_USER_PROP_IS_LOADING_CAPABILITIES]: isLoading,
          [MAP_CURRENT_USER_PROP_LOAD_CAPABILITIES_ERROR]: error,
        })
      ),

      // Add 'finished loading' prop.
      withProps(
        ({
           [MAP_CURRENT_USER_PROP_CURRENT_USER]:      currentUser,
           [MAP_CURRENT_USER_PROP_USER_CAPABILITIES]: userCapabilities,
         }) => ({[MAP_CURRENT_USER_PROP_FINISHED_LOADING]: currentUser && userCapabilities})
      )
    ),
    withProps(() => ({[MAP_CURRENT_USER_PROP_FINISHED_LOADING]: true}))
  ),
)(Component);


export default mapCurrentUser;


/**
 * Adds `hasEditableContent` & `editableContent` props to component, based on the `userCapabilities` prop.
 *
 * If all editable content is required, set `userCapabilities` to '*'.
 *
 * @return {function(*=, {userCapabilities: *}): {hasEditableContent: boolean, editableContent: *}}
 */
const mapCurrentUserState = (state) => ({
  [MAP_CURRENT_USER_PROP_IS_LOGGED_IN]:      selectIsLoggedIn(state),
  [MAP_CURRENT_USER_PROP_CURRENT_USER_ID]:   selectJwtUserIdIfNotExpired(state),
  [MAP_CURRENT_USER_PROP_IS_LOADING_USER]:   selectCurrentUserLoadInProgress(state),
  [MAP_CURRENT_USER_PROP_LOAD_USER_ERROR]:   selectCurrentUserLoadError(state),
  [MAP_CURRENT_USER_PROP_CURRENT_USER]:      selectCurrentUser(state),
  [MAP_CURRENT_USER_PROP_USER_CAPABILITIES]: selectCurrentUserCapabilities(state),
});



// WEBPACK FOOTER //
// ./src/components/auth/mapCurrentUser.js